export const calculateSystemMetrics = (
  solarPowerMW,
  outputPowerMW,
  batteryPowerMW
) => {
  const solar = solarPowerMW || 0;
  const load = outputPowerMW || 0;
  const batt = batteryPowerMW || 0; // +ve = discharging, -ve = charging

  // When battery is discharging (+batt), it's contributing to meet the load
  // When battery is charging (-batt), it's an additional demand on the system

  // Total system demand includes load plus any battery charging
  const totalDemand = load + Math.max(0, -batt);

  // Solar contribution to meeting the load specifically
  // This is the key fix: solar share should be based on what portion of the
  // actual load is met by solar, not total demand
  const solarContributionToLoad = Math.min(
    load,
    Math.max(0, solar - Math.max(0, -batt))
  );

  // Solar utilization: fraction of available solar actually used
  const solarUtilization =
    solar > 0 ? (Math.min(totalDemand, solar) / solar) * 100 : 0;

  // Solar share: what percentage of the load is met by solar
  const solarShare = load > 0 ? (solarContributionToLoad / load) * 100 : 0;

  // Unused solar (curtailment)
  const curtailmentMW = Math.max(0, solar - totalDemand);

  // Self-sustaining if battery is NOT draining
  const isSelfSustaining = batt <= 0;

  return {
    solar_utilization: Math.min(100, Math.max(0, solarUtilization)),
    solar_share: Math.min(100, Math.max(0, solarShare)),
    battery_net_power_mw: batt, // +discharge, -charge
    curtailment_mw: curtailmentMW, // unused solar
    is_self_sustaining: isSelfSustaining,
  };
};

/**
 * Format an uptime value in seconds to a human-readable string.
 * e.g. 90061 → "1d 1h", 3661 → "1h 1m", 61 → "1m"
 */
export const formatUptime = (uptimeSeconds) => {
  if (!uptimeSeconds) return "N/A";
  const hours = Math.floor(uptimeSeconds / 3600);
  const minutes = Math.floor((uptimeSeconds % 3600) / 60);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/**
 * Format a timestamp to a relative time string, falling back to absolute.
 * e.g. "Just now", "5m ago", "2h ago", "3d ago", or "DD/MM HH:MM"
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Returns Tailwind classes for a health status badge.
 * Accepted values: "excellent" | "good" | "fair" | "poor"
 */
export const getHealthStatusColor = (status) => {
  switch (status) {
    case "excellent": return "text-green-600 bg-green-100";
    case "good":      return "text-blue-600 bg-blue-100";
    case "fair":      return "text-yellow-600 bg-yellow-100";
    case "poor":      return "text-red-600 bg-red-100";
    default:          return "text-gray-600 bg-gray-100";
  }
};

// Function to add calculated fields to data
export const enhanceDataWithCalculations = (data) => {
  return data.map((item) => ({
    ...item,
    ...calculateSystemMetrics(
      item.solar_power_mW,
      item.output_power_mW,
      item.external_battery_power_mW
    ),
  }));
};
